import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import { useObservableAndState } from '@provider-portal/common/hooks/useObservableAndState';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';

import { MixpanelEventType } from '@provider-portal/common/services/MixpanelService';
import { PageTitle } from '@provider-portal/mp2-common/components/Page/PageTitle';

import { Box, Tab, Tabs } from '@provider-portal/design-common/kalep/components';

export interface TabInfo {
  id: string;
  labelIntlId: string;
  route: string;
}

export interface TabHeaderProps {
  id: string;
  titleIntlId: string;
  tabs: TabInfo[];
}

export function TabHeader({ id, titleIntlId, tabs }: TabHeaderProps) {
  const location = useLocation();
  const intl = useIntl();
  const currentPath = location.pathname;
  const { mixpanelService, accountService } = useCommonAppServices();
  const selectedProviderId = useObservableAndState(accountService.getSelectedProviderId$());

  // Determine current active tab based on pathname
  const currentTabIndex = useMemo(() => {
    const matchingTabIndex = tabs.findIndex((tab) => {
      return currentPath === tab.route || currentPath.startsWith(`${tab.route}/`);
    });

    return matchingTabIndex === -1 ? 0 : matchingTabIndex;
  }, [currentPath, tabs]);

  const onTabClick = useCallback(
    (index: number) => {
      mixpanelService.trackProviderEvent(MixpanelEventType.TAB_CLICKED, {
        providerId: selectedProviderId,
        currentModuleName: id,
        currentUrl: currentPath,
        navigatedUrl: tabs[index].route,
        tabName: tabs[index].id,
        currentTabName: tabs[currentTabIndex].id,
        tabRank: index + 1,
        currentTabRank: currentTabIndex + 1,
      });
    },
    [currentPath, currentTabIndex, id, mixpanelService, selectedProviderId, tabs],
  );

  return (
    <Box>
      <PageTitle title={intl.formatMessage({ id: titleIntlId })} />
      <Tabs value={currentTabIndex} variant="scrollable" sx={{ mb: '12px' }}>
        {tabs.map((tab, index) => (
          <Tab
            key={`${id}-${tab.id}`}
            label={intl.formatMessage({ id: tab.labelIntlId })}
            value={index}
            component={Link}
            to={tab.route}
            onClick={() => onTabClick(index)}
          />
        ))}
      </Tabs>
    </Box>
  );
}
